import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/auth-provider";
import { useOrg } from "../../auth/org-provider";
import { config } from "../../lib/config";

export type TranscriptionStatus = "idle" | "connecting" | "recording" | "stopping" | "error";

export interface UseLiveAudioTranscriptionOptions {
    onTranscription?: (text: string, isFinal: boolean) => void;
    onError?: (message: string) => void;
    onStop?: () => void;
    maxRecordingSeconds?: number;
}

export interface UseLiveAudioTranscriptionResult {
    status: TranscriptionStatus;
    recordingSeconds: number;
    liveTranscript: string;
    stream: MediaStream | null;
    startRecording: () => Promise<void>;
    stopRecording: () => void;
}

interface ServerMessage {
    type: "ready" | "error" | "transcription";
    message?: string;
    text?: string;
    isFinal?: boolean;
}

// AudioWorklet processor as inline blob — no static file needed
const WORKLET_CODE = `
class AudioProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0]?.[0];
    if (input) this.port.postMessage(Float32Array.from(input));
    return true;
  }
}
registerProcessor('live-audio-processor', AudioProcessor);
`;

function getWsUrl(): string {
    const base = (config.serviceHost as string).replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");
    return `${base}/ws/transcribe-audio`;
}

const DEFAULT_MAX_SECONDS = parseInt(process.env.REACT_APP_MAX_AUDIO_RECORDING_SECONDS ?? "300");

export function useLiveAudioTranscription(
    options: UseLiveAudioTranscriptionOptions = {}
): UseLiveAudioTranscriptionResult {
    const { onTranscription, onError, onStop, maxRecordingSeconds = DEFAULT_MAX_SECONDS } = options;
    const { user } = useAuth();
    const org = useOrg();

    const [status, setStatus] = useState<TranscriptionStatus>("idle");
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [liveTranscript, setLiveTranscript] = useState("");
    const [stream, setStream] = useState<MediaStream | null>(null);

    const wsRef = useRef<WebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const workletUrlRef = useRef<string | null>(null);
    const statusRef = useRef<TranscriptionStatus>("idle");

    // Keep statusRef in sync so ws.onclose callback sees up-to-date status
    useEffect(() => {
        statusRef.current = status;
    }, [status]);

    const cleanup = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
        timerRef.current = null;
        maxTimerRef.current = null;
        workletNodeRef.current?.disconnect();
        workletNodeRef.current = null;
        audioContextRef.current?.close();
        audioContextRef.current = null;
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        setStream(null);
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "stop" }));
            wsRef.current.close();
        }
        wsRef.current = null;
        if (workletUrlRef.current) {
            URL.revokeObjectURL(workletUrlRef.current);
            workletUrlRef.current = null;
        }
    }, []);

    useEffect(
        () => () => {
            cleanup();
        },
        [cleanup]
    );

    const stopRecording = useCallback(() => {
        setStatus("idle");
        setRecordingSeconds(0);
        setLiveTranscript("");
        cleanup();
        onStop?.();
    }, [cleanup, onStop]);

    const startRecording = useCallback(async () => {
        if (statusRef.current !== "idle") return;
        setStatus("connecting");
        setLiveTranscript("");
        setRecordingSeconds(0);

        try {
            const token = await user.getIdToken();
            const orgId = org.id;

            const mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
            });
            mediaStreamRef.current = mediaStream;
            setStream(mediaStream);

            const ws = new WebSocket(getWsUrl());
            wsRef.current = ws;

            ws.onopen = () => ws.send(JSON.stringify({ type: "auth", token, orgId }));

            ws.onmessage = async (event: MessageEvent<string>) => {
                const msg = JSON.parse(event.data) as ServerMessage;

                if (msg.type === "ready") {
                    await startAudioPipeline(mediaStream, ws);
                    setStatus("recording");
                    timerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
                    maxTimerRef.current = setTimeout(stopRecording, maxRecordingSeconds * 1_000);
                } else if (msg.type === "transcription" && msg.text) {
                    setLiveTranscript(msg.text);
                    onTranscription?.(msg.text, msg.isFinal ?? false);
                } else if (msg.type === "error") {
                    onError?.(msg.message ?? "Transcription error");
                    stopRecording();
                }
            };

            ws.onerror = () => {
                onError?.("Connection error");
                stopRecording();
            };
            ws.onclose = () => {
                if (statusRef.current === "recording") stopRecording();
            };
        } catch (err) {
            onError?.(err instanceof Error ? err.message : "Unknown error");
            cleanup();
            setStatus("error");
        }
    }, [user, org, stopRecording, onTranscription, onError, cleanup, maxRecordingSeconds]);

    async function startAudioPipeline(mediaStream: MediaStream, ws: WebSocket) {
        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;

        const url = URL.createObjectURL(new Blob([WORKLET_CODE], { type: "application/javascript" }));
        workletUrlRef.current = url;
        await audioContext.audioWorklet.addModule(url);

        const source = audioContext.createMediaStreamSource(mediaStream);
        const workletNode = new AudioWorkletNode(audioContext, "live-audio-processor");
        workletNodeRef.current = workletNode;

        workletNode.port.onmessage = (e: MessageEvent<Float32Array>) => {
            if (ws.readyState !== WebSocket.OPEN) return;
            const float32 = e.data;
            const int16 = new Int16Array(float32.length);
            for (let i = 0; i < float32.length; i++) {
                int16[i] = Math.max(-32768, Math.min(32767, float32[i] * 32768));
            }
            ws.send(int16.buffer);
        };

        source.connect(workletNode);
        // No connection to destination — we only need the message port
    }

    return { status, recordingSeconds, liveTranscript, stream, startRecording, stopRecording };
}
